/**
 * THE WORKBOOK IS AN APPLICATION — what is actually inside one Outform
 * estimating file, and what is broken in it.
 *
 * ⚠️ WHY THIS DIAGRAM EXISTS AT ALL. Erez, 29 Aug 2026: "the thing that I'm
 * most worried about is the fact that their excels are acting as a full
 * software. They're not just a database, they're also a backend." That was a
 * hypothesis. Every number on this canvas is the measurement of it, taken by
 * reading all 29 .xlsx files in the corpus with openpyxl — formulas, not
 * values. Atlas's ingest read what the cells SAY; nobody had read what they DO.
 *
 * ── THE FIVE MEASUREMENTS THE LAYOUT IS MAKING ─────────────────────────────
 *
 * 1 · 333,657 formulas across 29 workbooks — 52% of every non-empty cell.
 *     A file where more than half the cells are code is not a spreadsheet
 *     with some maths in it. It is a program that happens to be stored as a
 *     spreadsheet, and it has no repository, no tests and no reviewer.
 *
 * 2 · ⭐ 25 OF THE 26 SHEETS ARE HIDDEN. This is the finding that reframes the
 *     engagement. The estimator opens the file and sees `Estimate`. Behind it
 *     sit the labour rate book, five per-process cost models, three quantity
 *     scenarios and a 1001x69 scratch sheet — none of which they can inspect,
 *     and therefore none of which they can be blamed for or check. The columns
 *     here are ordered by visibility for exactly that reason.
 *
 * 3 · ⭐ THE LABOUR RATE TABLE IS TITLED "LABOR 2021", IN ALL SEVEN WORKBOOKS
 *     THAT HAVE ONE — including jobs quoted in 2026. A five-year-old cost
 *     basis, applied uniformly, invisible to the person quoting. Their own
 *     blueprint asks for a "cost price validity statement (time based)" in
 *     §3.2 as though it were a new requirement; this is the reason it is one.
 *
 * 4 · ⭐ THE MARGIN LADDER IS DEAD. `Margin` carries the right structure —
 *     QUANTITY / BUYOUT COST / LABOR UNIT COST / TOT. UNIT COST / EXT. COST /
 *     G. MARGIN % / UNIT SELL / EXT. SELL, stepped 0.20 → 0.60 — which is the
 *     40-65% perceived-value band idea their document is built on. Every
 *     computed cell in it reads #REF!. The feature they want to buy is already
 *     drawn in their own file and has been broken for long enough that nobody
 *     mentions it. 2,401 #REF! cells per workbook, in five of them.
 *
 * 5 · The red tone is reserved for STALE OR BROKEN, never for "hidden". Being
 *     hidden is a design choice; being wrong is a defect. Collapsing the two
 *     would let the reader dismiss the whole hidden layer as sloppiness, when
 *     most of it is competent work that simply cannot be seen.
 *
 * ── WHAT I DELIBERATELY DID NOT DRAW ───────────────────────────────────────
 * The per-sheet formula counts. They are real and I have them, but a number on
 * every one of 26 leaves turns a diagram into a table with wires, which is the
 * failure `skill://diagrams` §5 exists to prevent. Sheet dimensions earn their
 * place because they carry the argument (a 1001x69 hidden scratch sheet is the
 * point); formula counts do not.
 *
 * Source: scratch/formulas.py + scratch/drift.py over the 51-file corpus at
 * drop/Estimating Selection of Files. Counts are from
 * `243854 Sephora ESTIMATE BU - WITH TARIFF.xlsx`, the clone I took as baseline.
 */
import { Canvas, box, leaf, person, type Diagram } from "./c4";

// ═══ 1 · ONE WORKBOOK, READ AS A SYSTEM ══════════════════════════════════════

const ANATOMY: Diagram = (() => {
  const seen = box("wa-seen", "WHAT THE ESTIMATOR SEES", "col", [
    leaf("wa-est", "Estimate", "[271 x 32]", "Table2"),
  ], { tech: "[1 of 26 sheets]" });

  // The rate book. Four of these six are stale or broken — and every one of
  // them is hidden, so the person quoting cannot know that.
  const rates = box("wa-rates", "HIDDEN · THE RATE BOOK", "col", [
    leaf("wa-labor", "Laborrates", "[LABOR 2021]", "Clock", "gate"),
    leaf("wa-prolabor", "Prolaborrate", "[#REF!]", "Clock", "gate"),
    leaf("wa-margin", "Margin", "[#REF!]", "Percent", "gate"),
    leaf("wa-cmargin", "CMargin", "[#DIV/0!]", "Percent", "gate"),
    leaf("wa-cm", "CM", undefined, "Percent"),
    leaf("wa-cmlist", "Client CM LIST", "[240 rows]", "Users"),
  ], { tech: "[6 sheets]" });

  // Five real manufacturing cost models. This is the part of the engine that
  // is genuinely good work — and it is the part nobody can see.
  const process = box("wa-proc", "HIDDEN · PROCESS COST MODELS", "col", [
    leaf("wa-mount", "Mounting", "[413 rows]", "Wrench"),
    leaf("wa-diemake", "Diemaking", "[197 rows]", "Wrench"),
    leaf("wa-diecut", "Diecutting", "[440 rows]", "Wrench"),
    leaf("wa-strip", "Stripping", "[701 rows]", "Wrench"),
    leaf("wa-clean", "Cleaning", "[476 rows]", "Wrench"),
  ], { tech: "[5 sheets]" });

  const scratch = box("wa-scratch", "HIDDEN · SCRATCH & ROLL-UP", "col", [
    leaf("wa-work", "Work", "[1001 x 69]", "Grid3x3"),
    leaf("wa-dev", "Development", undefined, "Calculator"),
    leaf("wa-jcost", "JCost", undefined, "Calculator"),
    leaf("wa-jobanal", "Job Analysis", undefined, "Calculator"),
    leaf("wa-frontend", "Front End", undefined, "Calculator"),
    leaf("wa-dash", "Dashboard", "[209 rows]", "LayoutDashboard"),
    box("wa-qty", "SCENARIOS", "row", [
      leaf("wa-q1", "Qty 1"),
      leaf("wa-q2", "Qty 2"),
      leaf("wa-q3", "Qty 3"),
    ], { pad: 0, gap: 9, depth: 2 }),
  ], { tech: "[14 sheets]" });

  return {
    key: "outform-workbook-anatomy",
    title: "One estimating workbook, read as software",
    subtitle:
      "243854 Sephora ESTIMATE BU — 26 sheets - 41,171 formulas - 25 of 26 hidden. Measured by reading formulas, not values.",
    cols: [
      { root: person("wa-user", "Estimator") },
      { root: seen },
      { root: rates },
      { root: process },
      { root: scratch },
    ],
    // ⚠️ EVERY WIRE HERE IS BETWEEN ADJACENT COLUMNS, AND THAT IS DELIBERATE.
    // c4.tsx paints the wire SVG first and the boxes over it, so a wire whose
    // path crosses an intervening column is drawn UNDER that column's fill and
    // silently disappears (skill://diagrams §7). My first draft had two
    // long-haul wires — a roll-up from scratch back to Estimate, and a
    // no-repo/no-truth link across the clone diagram — and both would have
    // vanished. Re-reading them as a left-to-right chain turned out to be the
    // truer story anyway: rates are looked up, applied per process, then rolled
    // up. The constraint improved the diagram rather than compromising it.
    wires: [
      // 1 — the whole human interface, one sheet wide.
      { a: "wa-user", b: "wa-est", as: "r", bs: "l", num: 1, label: "the only sheet they open" },
      // 2 — red at the RATE BOOK end: the break is in the rates themselves,
      // not in the reference to them.
      { a: "wa-est", b: "wa-rates", as: "r", bs: "l", num: 2, red: true, label: "rates - 5 yrs stale" },
      { a: "wa-rates", b: "wa-proc", as: "r", bs: "l", num: 3, label: "applied per process" },
      { a: "wa-proc", b: "wa-scratch", as: "r", bs: "l", num: 4, dashed: true, label: "3,712 x-sheet refs" },
    ],
    legend: [
      { style: "solid", label: "what the estimator can see and touch — one sheet of twenty-six, and every judgement below it is taken on their behalf" },
      { style: "dash", label: "hidden sheets — 25 of 26, holding the labour rate book, five manufacturing cost models and a 1001x69 scratch pad" },
      { style: "red", label: "stale or broken, and hidden, which is why neither has been noticed: labour rates titled LABOR 2021 in all seven workbooks, and 2,401 #REF! cells per file — including the entire Margin ladder their own §7 perceived-value pricing depends on" },
    ],
    padBottom: 76,
  };
})();

// ═══ 2 · SEVEN JOBS, ONE PROGRAM ═════════════════════════════════════════════

/**
 * The second question, and the one that decides whether this is a rebuild or a
 * port: is there ONE engine here, or seven?
 *
 * I diffed the five clone workbooks formula-by-formula, cell by cell. They are
 * 99.6% identical — 40,870 of 41,024 shared formula cells byte-for-byte the
 * same across five different clients. That is the good news and it is large:
 * there is one program to reimplement, not seven.
 *
 * ⚠️ AND I HELD MYSELF TO THE HARD VERSION OF THE CLAIM. My first pass counted
 * 63 "logic differences". Most were an artefact of my own method: each job's
 * Estimate sheet has a different number of part rows, so the blocks below shift
 * and the same cell address means a different thing in two files. Normalising
 * row numbers away and re-testing left THREE divergences that alignment cannot
 * explain — identical SUM over an identical range, with a 1% uplift in one file
 * and not the other. Three, not sixty-three. The honest number is the smaller
 * one, and it is still enough to prove the point: nobody can tell which of the
 * seven variants is correct, because nothing anywhere records what correct is.
 */
const CLONES: Diagram = (() => {
  const src = box("cl-src", "WHAT THERE IS ONE OF", "col", [
    leaf("cl-engine", "Estimating engine", "[26 sheets]", "Cpu"),
    leaf("cl-nowhere", "Kept nowhere", "[no repo, no owner]", "FileQuestion", "gate"),
  ]);

  const copies = box("cl-copies", "COPIED INTO EVERY JOB", "col", [
    leaf("cl-seph", "Sephora 243854", "[41,171]", "FileSpreadsheet"),
    leaf("cl-lowe1", "Lowe's 244524R4", "[41,183]", "FileSpreadsheet"),
    leaf("cl-lowe2", "Lowe's MISC R4", "[41,159]", "FileSpreadsheet"),
    leaf("cl-winky", "Winky Lux 255401", "[41,159]", "FileSpreadsheet"),
    leaf("cl-loreal", "L'Oreal 261152", "[41,140]", "FileSpreadsheet"),
  ], { tech: "[formula cells]" });

  const drift = box("cl-drift", "WHAT DIVERGED", "col", [
    leaf("cl-same", "99.6% identical", "[40,870 of 41,024]", "Equal"),
    leaf("cl-offset", "~135 row offsets", "[benign]", "MoveVertical"),
    leaf("cl-real", "3 real divergences", "[same range, +1%]", "TriangleAlert", "gate"),
    leaf("cl-truth", "No correct version", "[nothing records it]", "HelpCircle", "gate"),
  ]);

  return {
    key: "outform-clone-drift",
    title: "Seven jobs, one program, no source of truth",
    subtitle:
      "Five clone workbooks diffed formula-by-formula. One engine to rebuild — but nobody can say which copy is right.",
    cols: [{ root: src }, { root: copies }, { root: drift }],
    // Adjacent-column wires only — see the note on ANATOMY above. The link I
    // wanted from "kept nowhere" to "no correct version" would have crossed the
    // clone column and been painted over; the two red leaves carry it instead.
    wires: [
      { a: "cl-engine", b: "cl-copies", as: "r", bs: "l", num: 1, dashed: true, label: "copy-paste, per job" },
      { a: "cl-copies", b: "cl-drift", as: "r", bs: "l", num: 2, label: "cell-by-cell diff" },
    ],
    legend: [
      { style: "solid", label: "the good news, and it is the larger half: 99.6% identical across five clients — 40,870 of 41,024 shared formula cells byte-for-byte the same. ONE engine to reimplement, not seven. This is a port, not a rebuild" },
      { style: "dash", label: "how a new job starts: the last similar workbook is copied and edited. No repository, no version, no owner — which is why ~135 cells drift apart per pair and nobody has ever seen it" },
      { style: "red", label: "the part that is not benign: three cells summing an IDENTICAL range with a 1% uplift in one file and not the other. Three, not the sixty-three my first pass claimed — the rest were my own row-offset artefact. Small, and unresolvable, because no version is marked correct" },
    ],
    padBottom: 76,
  };
})();

/** One workbook, read as software: 26 sheets, 25 hidden, and what is broken. */
export function OutformWorkbookAnatomy() {
  return <Canvas d={ANATOMY} mode="contrast" />;
}

/** Five clones of one engine, diffed: 99.6% identical, and no source of truth. */
export function OutformCloneDrift() {
  return <Canvas d={CLONES} mode="contrast" />;
}
